/**
 * GRANULLO PLATFORM EVOLUTION — Baseline Signals Context
 * 
 * Phase 2: Baseline Signal System (System Telemetry)
 * 
 * Every program must define expected behavior baselines.
 * Signals define safe operating conditions that Granullo monitors for drift.
 * 
 * FOUNDER DIRECTIVE: SINGLE SOURCE OF TRUTH REQUIREMENT
 * - ProgramIntegrityContext is the canonical owner
 * - BaselineSignalsContext is DERIVED from ProgramIntegrityContext
 * - No independent writable shadow state may exist
 * 
 * Required canonical structure inside Program Object:
 * ProgramObject {
 *   blueprint_graph
 *   baseline_signals
 * }
 * 
 * ARCHITECTURAL CONSTRAINT:
 * - Signals MUST be attached to blueprint nodes or edges
 * - Signals are NOT decorative or UI-only
 * - Signals feed into: Monitoring Engine, Decision Matrix, Exposure Engine, Commit Economy
 * 
 * Signal Structure:
 * - Baseline: normal_range (min/max/target)
 * - Thresholds: warning, critical, emergency, catastrophic
 * - Monitoring: refresh rate, aggregation method
 * 
 * This is like:
 * - Oxygen saturation in surgery (drift = immediate alert)
 * - Aircraft separation in ATC (drift = rerouting)
 * - Vital signs in ICU (drift = escalation)
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useProgramIntegrityOptional } from './ProgramIntegrityContext';

// ============================================================================
// SIGNAL CATEGORY DEFINITIONS
// ============================================================================

/**
 * Signal Categories — Types of metrics to monitor
 */
export const SIGNAL_CATEGORIES = {
  VELOCITY: 'velocity',           // Transaction/flow speed
  CONCENTRATION: 'concentration', // Distribution imbalance
  DISTRIBUTION: 'distribution',   // Spread/coverage
  COMPLIANCE: 'compliance',       // Policy adherence
  CAPACITY: 'capacity',           // Throughput limits
  LATENCY: 'latency',             // Processing delays
  ANOMALY: 'anomaly'              // Unexpected patterns
};

/**
 * Signal Category Metadata
 */
export const SIGNAL_CATEGORY_METADATA = {
  [SIGNAL_CATEGORIES.VELOCITY]: {
    label: 'Velocity',
    description: 'Transaction or flow speed metrics',
    icon: 'gauge',
    color: '#3B82F6',
    examples: ['Transaction rate', 'Redemption velocity', 'Processing speed']
  },
  [SIGNAL_CATEGORIES.CONCENTRATION]: {
    label: 'Concentration',
    description: 'Distribution imbalance detection',
    icon: 'pie-chart',
    color: '#EFD6B8',
    examples: ['Merchant concentration', 'Geographic concentration', 'Beneficiary clustering']
  },
  [SIGNAL_CATEGORIES.DISTRIBUTION]: {
    label: 'Distribution',
    description: 'Spread and coverage metrics',
    icon: 'scatter-chart',
    color: '#10B981',
    examples: ['Geographic spread', 'Category usage', 'Beneficiary distribution']
  },
  [SIGNAL_CATEGORIES.COMPLIANCE]: {
    label: 'Compliance',
    description: 'Policy adherence monitoring',
    icon: 'shield-check',
    color: '#D7D2EA',
    examples: ['Policy violations', 'Decline rate', 'Rule exceptions']
  },
  [SIGNAL_CATEGORIES.CAPACITY]: {
    label: 'Capacity',
    description: 'Throughput and limit monitoring',
    icon: 'server',
    color: '#EF4444',
    examples: ['Throughput vs capacity', 'Buffer utilization', 'Queue depth']
  },
  [SIGNAL_CATEGORIES.LATENCY]: {
    label: 'Latency',
    description: 'Processing delay detection',
    icon: 'timer',
    color: '#D4A88C',
    examples: ['Processing time', 'Settlement delay', 'Execution lag']
  },
  [SIGNAL_CATEGORIES.ANOMALY]: {
    label: 'Anomaly',
    description: 'Unexpected pattern detection',
    icon: 'alert-triangle',
    color: '#F97316',
    examples: ['Unusual spikes', 'Pattern breaks', 'Cluster detection']
  }
};

// ============================================================================
// REFRESH RATE DEFINITIONS
// ============================================================================

/**
 * Signal Refresh Rates
 */
export const REFRESH_RATES = {
  REALTIME: 'realtime',
  ONE_MINUTE: '1min',
  FIVE_MINUTES: '5min',
  HOURLY: 'hourly',
  DAILY: 'daily'
};

/**
 * Refresh Rate Metadata
 */
export const REFRESH_RATE_METADATA = {
  [REFRESH_RATES.REALTIME]: { label: 'Real-time', seconds: 0, description: 'Continuous monitoring' },
  [REFRESH_RATES.ONE_MINUTE]: { label: 'Every minute', seconds: 60, description: '60 second intervals' },
  [REFRESH_RATES.FIVE_MINUTES]: { label: 'Every 5 minutes', seconds: 300, description: '5 minute intervals' },
  [REFRESH_RATES.HOURLY]: { label: 'Hourly', seconds: 3600, description: 'Hourly snapshots' },
  [REFRESH_RATES.DAILY]: { label: 'Daily', seconds: 86400, description: 'Daily aggregations' }
};

// ============================================================================
// AGGREGATION METHODS
// ============================================================================

/**
 * Signal Aggregation Methods
 */
export const AGGREGATION_METHODS = {
  INSTANT: 'instant',
  AVERAGE: 'average',
  SUM: 'sum',
  MAX: 'max',
  MIN: 'min',
  PERCENTILE_95: 'p95',
  PERCENTILE_99: 'p99'
};

// ============================================================================
// THRESHOLD LEVELS
// ============================================================================

/**
 * Threshold severity levels for drift detection
 * FOUNDER DIRECTIVE: Complete severity ladder required for Phase 3 authority routing
 */
export const THRESHOLD_LEVELS = {
  NORMAL: 'normal',
  WARNING: 'warning',
  CRITICAL: 'critical',
  EMERGENCY: 'emergency',
  CATASTROPHIC: 'catastrophic'
};

/**
 * Threshold Level Metadata
 */
export const THRESHOLD_LEVEL_METADATA = {
  [THRESHOLD_LEVELS.NORMAL]: { 
    label: 'Normal', 
    color: '#10B981', 
    bgColor: 'bg-green-100 dark:bg-slate-900/20',
    textColor: 'text-green-700 dark:text-green-400',
    description: 'Operating within expected bounds'
  },
  [THRESHOLD_LEVELS.WARNING]: { 
    label: 'Warning', 
    color: '#EFD6B8', 
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
    textColor: 'text-yellow-700 dark:text-yellow-400',
    description: 'Approaching threshold, attention needed'
  },
  [THRESHOLD_LEVELS.CRITICAL]: { 
    label: 'Critical', 
    color: '#F97316', 
    bgColor: 'bg-orange-100 dark:bg-orange-900/20',
    textColor: 'text-orange-700 dark:text-orange-400',
    description: 'Threshold breached, action required'
  },
  [THRESHOLD_LEVELS.EMERGENCY]: { 
    label: 'Emergency', 
    color: '#EF4444', 
    bgColor: 'bg-red-100 dark:bg-red-900/20',
    textColor: 'text-red-700 dark:text-red-400',
    description: 'Critical state, immediate intervention needed'
  },
  [THRESHOLD_LEVELS.CATASTROPHIC]: { 
    label: 'Catastrophic', 
    color: '#7F1D1D', 
    bgColor: 'bg-red-200 dark:bg-red-950/40',
    textColor: 'text-red-900 dark:text-red-300',
    description: 'System-wide failure, maximum authority escalation'
  }
};

// ============================================================================
// SIGNAL SCHEMA
// ============================================================================

/**
 * Create a new baseline signal
 * 
 * FOUNDER DIRECTIVE: Deterministic signal schema with complete severity ladder
 * Required fields:
 * - signal_id
 * - node_scope (nodeRef)
 * - normal_range (baseline)
 * - warning_threshold
 * - critical_threshold
 * - catastrophic_threshold
 */
export function createBaselineSignal({
  id,
  programId,
  name,
  description = '',
  category,
  nodeRef = null,
  edgeRef = null,
  baseline = {},
  thresholds = {},
  refreshRate = REFRESH_RATES.FIVE_MINUTES,
  aggregation = AGGREGATION_METHODS.AVERAGE,
  window = 300,
  decisionMatrixEntryRef = null,
  enabled = true
}) {
  return {
    // FOUNDER DIRECTIVE: signal_id
    signal_id: id || `signal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    id: id || `signal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    programId,
    
    // Identity
    name,
    description,
    category,
    
    // FOUNDER DIRECTIVE: node_scope (Attachment - must have nodeRef OR edgeRef)
    node_scope: nodeRef || edgeRef,
    nodeRef,
    edgeRef,
    
    // FOUNDER DIRECTIVE: normal_range (Safe operating bounds)
    normal_range: {
      min: baseline.min ?? 0,
      max: baseline.max ?? 100,
      target: baseline.target ?? 50,
      unit: baseline.unit || 'units'
    },
    baseline: {
      min: baseline.min ?? 0,
      max: baseline.max ?? 100,
      target: baseline.target ?? 50,
      unit: baseline.unit || 'units'
    },
    
    // FOUNDER DIRECTIVE: Complete threshold hierarchy for Phase 3 authority routing
    // warning_threshold, critical_threshold, catastrophic_threshold
    warning_threshold: thresholds.warning ?? 0.25,     // 25% deviation
    critical_threshold: thresholds.critical ?? 0.50,   // 50% deviation
    emergency_threshold: thresholds.emergency ?? 0.75, // 75% deviation
    catastrophic_threshold: thresholds.catastrophic ?? 0.90, // 90% deviation - system-wide failure
    
    // Legacy thresholds object (for backward compatibility)
    thresholds: {
      warning: thresholds.warning ?? 0.25,
      critical: thresholds.critical ?? 0.50,
      emergency: thresholds.emergency ?? 0.75,
      catastrophic: thresholds.catastrophic ?? 0.90
    },
    
    // Monitoring configuration
    refreshRate,
    aggregation,
    window, // Time window in seconds
    
    // Decision matrix link
    decisionMatrixEntryRef,
    
    // State
    enabled,
    currentValue: null,
    currentStatus: THRESHOLD_LEVELS.NORMAL,
    lastEvaluatedAt: null,
    
    // Metadata
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

// ============================================================================
// SIGNAL TEMPLATES BY PROGRAM TYPE
// ============================================================================

/**
 * Pre-defined signal templates for common program types
 */
export const SIGNAL_TEMPLATES = {
  CBDC: [
    {
      name: 'Transaction Velocity',
      category: SIGNAL_CATEGORIES.VELOCITY,
      baseline: { min: 100, max: 500, target: 300, unit: 'tx/min' },
      thresholds: { warning: 600, critical: 800, emergency: 1000 },
      description: 'Transaction processing rate through the network'
    },
    {
      name: 'Merchant Concentration',
      category: SIGNAL_CATEGORIES.CONCENTRATION,
      baseline: { min: 0, max: 20, target: 10, unit: '%' },
      thresholds: { warning: 25, critical: 35, emergency: 50 },
      description: 'Top 5 merchant share of total transactions'
    },
    {
      name: 'Redemption Rate',
      category: SIGNAL_CATEGORIES.VELOCITY,
      baseline: { min: 60, max: 85, target: 75, unit: '%' },
      thresholds: { warning: 55, critical: 40, emergency: 25 },
      description: 'Percentage of disbursed funds being utilized'
    },
    {
      name: 'Geographic Spread',
      category: SIGNAL_CATEGORIES.DISTRIBUTION,
      baseline: { min: 10, max: 50, target: 20, unit: 'regions' },
      thresholds: { warning: 8, critical: 5, emergency: 3 },
      description: 'Number of regions with active transactions'
    },
    {
      name: 'Policy Violations',
      category: SIGNAL_CATEGORIES.COMPLIANCE,
      baseline: { min: 0, max: 2, target: 0.5, unit: '%' },
      thresholds: { warning: 5, critical: 10, emergency: 20 },
      description: 'Percentage of transactions violating policy rules'
    }
  ],
  
  EMPLOYEE_BENEFITS: [
    {
      name: 'Category Usage Rate',
      category: SIGNAL_CATEGORIES.DISTRIBUTION,
      baseline: { min: 40, max: 70, target: 55, unit: '%' },
      thresholds: { warning: 30, critical: 15, emergency: 5 },
      description: 'Distribution of spend across benefit categories'
    },
    {
      name: 'Merchant Concentration',
      category: SIGNAL_CATEGORIES.CONCENTRATION,
      baseline: { min: 0, max: 30, target: 15, unit: '%' },
      thresholds: { warning: 40, critical: 60, emergency: 80 },
      description: 'Top 10 merchant share of total spend'
    },
    {
      name: 'Decline Rate',
      category: SIGNAL_CATEGORIES.COMPLIANCE,
      baseline: { min: 0, max: 5, target: 2, unit: '%' },
      thresholds: { warning: 8, critical: 15, emergency: 25 },
      description: 'Percentage of declined transactions'
    },
    {
      name: 'Expiry Utilization',
      category: SIGNAL_CATEGORIES.VELOCITY,
      baseline: { min: 80, max: 100, target: 90, unit: '%' },
      thresholds: { warning: 70, critical: 50, emergency: 30 },
      description: 'Utilization rate before benefit expiry'
    }
  ],
  
  DONATION: [
    {
      name: 'Donation Velocity',
      category: SIGNAL_CATEGORIES.VELOCITY,
      baseline: { min: 10, max: 50, target: 25, unit: 'donations/hr' },
      thresholds: { warning: 5, critical: 2, emergency: 1 },
      description: 'Rate of incoming donations'
    },
    {
      name: 'Cause Concentration',
      category: SIGNAL_CATEGORIES.CONCENTRATION,
      baseline: { min: 0, max: 40, target: 20, unit: '%' },
      thresholds: { warning: 50, critical: 70, emergency: 90 },
      description: 'Share of donations going to single cause'
    },
    {
      name: 'Beneficiary Distribution',
      category: SIGNAL_CATEGORIES.DISTRIBUTION,
      baseline: { min: 0, max: 0.3, target: 0.15, unit: 'gini' },
      thresholds: { warning: 0.4, critical: 0.6, emergency: 0.8 },
      description: 'Fairness of distribution (Gini coefficient)'
    },
    {
      name: 'Partner Execution Delay',
      category: SIGNAL_CATEGORIES.LATENCY,
      baseline: { min: 0, max: 24, target: 12, unit: 'hours' },
      thresholds: { warning: 48, critical: 72, emergency: 96 },
      description: 'Time from allocation to partner execution'
    },
    {
      name: 'Impact Variance',
      category: SIGNAL_CATEGORIES.ANOMALY,
      baseline: { min: 0, max: 15, target: 5, unit: '%' },
      thresholds: { warning: 25, critical: 40, emergency: 60 },
      description: 'Variance in reported impact vs expected'
    }
  ],
  
  CIRCULARITY: [
    {
      name: 'Throughput vs Capacity',
      category: SIGNAL_CATEGORIES.CAPACITY,
      baseline: { min: 60, max: 85, target: 75, unit: '%' },
      thresholds: { warning: 90, critical: 95, emergency: 99 },
      description: 'Current throughput relative to system capacity'
    },
    {
      name: 'Node Concentration',
      category: SIGNAL_CATEGORIES.CONCENTRATION,
      baseline: { min: 0, max: 25, target: 10, unit: '%' },
      thresholds: { warning: 35, critical: 50, emergency: 70 },
      description: 'Share of flow through single collector'
    },
    {
      name: 'Supply Chain Depth',
      category: SIGNAL_CATEGORIES.CONCENTRATION,
      baseline: { min: 2, max: 3, target: 2.5, unit: 'levels' },
      thresholds: { warning: 4, critical: 5, emergency: 6 },
      description: 'Average depth of supply chain'
    },
    {
      name: 'Processing Latency',
      category: SIGNAL_CATEGORIES.LATENCY,
      baseline: { min: 0, max: 100, target: 50, unit: 'ms' },
      thresholds: { warning: 200, critical: 500, emergency: 1000 },
      description: 'Average processing time per unit'
    },
    {
      name: 'Anomaly Clusters',
      category: SIGNAL_CATEGORIES.ANOMALY,
      baseline: { min: 0, max: 5, target: 2, unit: '%' },
      thresholds: { warning: 10, critical: 20, emergency: 35 },
      description: 'Percentage of anomalous transaction clusters'
    }
  ],
  
  VC_FUNDING: [
    {
      name: 'Milestone Completion Rate',
      category: SIGNAL_CATEGORIES.VELOCITY,
      baseline: { min: 70, max: 100, target: 85, unit: '%' },
      thresholds: { warning: 60, critical: 40, emergency: 20 },
      description: 'On-time milestone completion percentage'
    },
    {
      name: 'Vendor Concentration',
      category: SIGNAL_CATEGORIES.CONCENTRATION,
      baseline: { min: 0, max: 30, target: 15, unit: '%' },
      thresholds: { warning: 40, critical: 60, emergency: 80 },
      description: 'Spend concentration with top vendors'
    },
    {
      name: 'Burn Rate vs Projection',
      category: SIGNAL_CATEGORIES.VELOCITY,
      baseline: { min: 80, max: 100, target: 95, unit: '%' },
      thresholds: { warning: 120, critical: 150, emergency: 200 },
      description: 'Actual burn rate relative to projection'
    }
  ]
};

// ============================================================================
// DRIFT DETECTION
// ============================================================================

/**
 * Evaluate signal drift
 * FOUNDER DIRECTIVE: Complete severity ladder evaluation for Phase 3 authority routing
 */
export function evaluateSignalDrift(signal, currentValue) {
  if (!signal || currentValue === null || currentValue === undefined) {
    return null;
  }
  
  const baseline = signal.normal_range || signal.baseline;
  const thresholds = signal.thresholds;
  const { min, max, target } = baseline;
  
  // Calculate deviation from target
  const deviation = Math.abs(currentValue - target);
  const range = max - min;
  const deviationPercent = range > 0 ? (deviation / range) * 100 : 0;
  
  // Check if value is out of bounds
  const isAboveMax = currentValue > max;
  const isBelowMin = currentValue < min;
  const isOutOfBounds = isAboveMax || isBelowMin;
  
  // Determine threshold level (complete severity ladder)
  let status = THRESHOLD_LEVELS.NORMAL;
  let breachedThreshold = null;
  
  if (isOutOfBounds) {
    // For out-of-bounds, check which threshold is breached
    if (isAboveMax) {
      if (currentValue >= (thresholds.catastrophic || signal.catastrophic_threshold)) {
        status = THRESHOLD_LEVELS.CATASTROPHIC;
      } else if (currentValue >= (thresholds.emergency || signal.emergency_threshold)) {
        status = THRESHOLD_LEVELS.EMERGENCY;
      } else if (currentValue >= (thresholds.critical || signal.critical_threshold)) {
        status = THRESHOLD_LEVELS.CRITICAL;
      } else if (currentValue >= (thresholds.warning || signal.warning_threshold)) {
        status = THRESHOLD_LEVELS.WARNING;
      }
    } else {
      // For below-min signals (like redemption rate), lower is worse
      if (currentValue <= (thresholds.catastrophic || signal.catastrophic_threshold)) {
        status = THRESHOLD_LEVELS.CATASTROPHIC;
      } else if (currentValue <= (thresholds.emergency || signal.emergency_threshold)) {
        status = THRESHOLD_LEVELS.EMERGENCY;
      } else if (currentValue <= (thresholds.critical || signal.critical_threshold)) {
        status = THRESHOLD_LEVELS.CRITICAL;
      } else if (currentValue <= (thresholds.warning || signal.warning_threshold)) {
        status = THRESHOLD_LEVELS.WARNING;
      }
    }
    breachedThreshold = status;
  } else {
    // Within bounds but check deviation percentage
    if (deviationPercent >= 90) status = THRESHOLD_LEVELS.CATASTROPHIC;
    else if (deviationPercent >= 75) status = THRESHOLD_LEVELS.EMERGENCY;
    else if (deviationPercent >= 50) status = THRESHOLD_LEVELS.CRITICAL;
    else if (deviationPercent >= 25) status = THRESHOLD_LEVELS.WARNING;
  }
  
  return {
    signalId: signal.signal_id || signal.id,
    signalName: signal.name,
    currentValue,
    baselineTarget: target,
    baselineMin: min,
    baselineMax: max,
    deviation,
    deviationPercent: Math.round(deviationPercent * 10) / 10,
    status,
    breachedThreshold,
    isOutOfBounds,
    isCatastrophic: status === THRESHOLD_LEVELS.CATASTROPHIC,
    direction: currentValue > target ? 'above' : currentValue < target ? 'below' : 'on_target',
    evaluatedAt: new Date().toISOString()
  };
}

/**
 * Create a drift event for the Decision Matrix Engine
 */
export function createDriftEvent(signal, driftEvaluation) {
  if (!signal || !driftEvaluation) return null;
  
  return {
    signalRef: signal.id,
    signalName: signal.name,
    currentValue: driftEvaluation.currentValue,
    baselineValue: driftEvaluation.baselineTarget,
    thresholdBreached: driftEvaluation.breachedThreshold,
    duration: 0, // To be calculated by monitoring layer
    timestamp: new Date(),
    nodeRef: signal.nodeRef,
    edgeRef: signal.edgeRef,
    category: signal.category,
    decisionMatrixEntryRef: signal.decisionMatrixEntryRef
  };
}

// ============================================================================
// CONTEXT
// ============================================================================

const BaselineSignalsContext = createContext(null);

/**
 * BaselineSignalsProvider — DERIVED from ProgramIntegrityContext
 * 
 * FOUNDER DIRECTIVE: SINGLE SOURCE OF TRUTH REQUIREMENT
 * - This context PROJECTS state from ProgramIntegrityContext for UI purposes
 * - All mutations propagate back to ProgramIntegrityContext
 * - No independent writable shadow state
 */
export function BaselineSignalsProvider({ children }) {
  // FOUNDER DIRECTIVE: Derive signals from ProgramIntegrityContext
  // If ProgramIntegrityContext is not available (e.g., in demo mode), 
  // fall back to local state for backwards compatibility
  // NOTE: Using useProgramIntegrityOptional() to avoid rules-of-hooks violation
  const programIntegrityContext = useProgramIntegrityOptional();
  
  // Local state fallback for demo mode
  const [localSignals, setLocalSignals] = useState([]);
  const [signalHistory, setSignalHistory] = useState({}); // signalId -> array of evaluations
  const [activeAlerts, setActiveAlerts] = useState([]);
  
  // Monitoring state (UI-only, not persisted in Program Object)
  const [monitoringEnabled, setMonitoringEnabled] = useState(false);
  const [lastScanAt, setLastScanAt] = useState(null);
  
  // FOUNDER DIRECTIVE: signals is DERIVED from ProgramIntegrityContext
  // or falls back to local state for demo/standalone usage
  const signals = useMemo(() => {
    if (programIntegrityContext?.integrity?.baselineSignals) {
      return programIntegrityContext.integrity.baselineSignals;
    }
    return localSignals;
  }, [programIntegrityContext?.integrity?.baselineSignals, localSignals]);
  
  // Internal setter that propagates to ProgramIntegrityContext or local state
  const setSignals = useCallback((signalsOrUpdater) => {
    const newSignals = typeof signalsOrUpdater === 'function' 
      ? signalsOrUpdater(signals)
      : signalsOrUpdater;
    
    if (programIntegrityContext) {
      // FOUNDER DIRECTIVE: Propagate to canonical owner
      programIntegrityContext.updateIntegrity({ baselineSignals: newSignals });
    } else {
      // Fallback for demo mode
      setLocalSignals(newSignals);
    }
  }, [signals, programIntegrityContext]);
  
  // ============================================================================
  // SIGNAL MANAGEMENT
  // ============================================================================
  
  /**
   * Add a new signal
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const addSignal = useCallback((signalData) => {
    const signal = createBaselineSignal(signalData);
    setSignals(prev => [...prev, signal]);
    return signal;
  }, [setSignals]);
  
  /**
   * Add multiple signals from a template
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const addSignalsFromTemplate = useCallback((programId, templateKey) => {
    const template = SIGNAL_TEMPLATES[templateKey];
    if (!template) return [];
    
    const newSignals = template.map(signalTemplate => 
      createBaselineSignal({
        ...signalTemplate,
        programId
      })
    );
    
    setSignals(prev => [...prev, ...newSignals]);
    return newSignals;
  }, [setSignals]);
  
  /**
   * Update a signal
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const updateSignal = useCallback((signalId, updates) => {
    setSignals(prev => prev.map(s => 
      s.id === signalId || s.signal_id === signalId
        ? { ...s, ...updates, updatedAt: new Date().toISOString() }
        : s
    ));
  }, [setSignals]);
  
  /**
   * Remove a signal
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const removeSignal = useCallback((signalId) => {
    setSignals(prev => prev.filter(s => s.id !== signalId && s.signal_id !== signalId));
    setSignalHistory(prev => {
      const newHistory = { ...prev };
      delete newHistory[signalId];
      return newHistory;
    });
  }, [setSignals]);
  
  /**
   * Enable/disable a signal
   */
  const toggleSignal = useCallback((signalId, enabled) => {
    updateSignal(signalId, { enabled });
  }, [updateSignal]);
  
  /**
   * Attach signal to a blueprint node
   */
  const attachToNode = useCallback((signalId, nodeRef) => {
    updateSignal(signalId, { nodeRef, edgeRef: null });
  }, [updateSignal]);
  
  /**
   * Attach signal to a blueprint edge
   */
  const attachToEdge = useCallback((signalId, edgeRef) => {
    updateSignal(signalId, { edgeRef, nodeRef: null });
  }, [updateSignal]);
  
  /**
   * Link signal to decision matrix entry
   */
  const linkToDecisionMatrix = useCallback((signalId, decisionMatrixEntryRef) => {
    updateSignal(signalId, { decisionMatrixEntryRef });
  }, [updateSignal]);
  
  // ============================================================================
  // DRIFT EVALUATION
  // ============================================================================
  
  /**
   * Evaluate a single signal
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const evaluateSignal = useCallback((signalId, currentValue) => {
    const signal = signals.find(s => s.id === signalId || s.signal_id === signalId);
    if (!signal) return null;
    
    const evaluation = evaluateSignalDrift(signal, currentValue);
    
    // Update signal state
    setSignals(prev => prev.map(s => 
      (s.id === signalId || s.signal_id === signalId)
        ? { 
            ...s, 
            currentValue, 
            currentStatus: evaluation.status,
            lastEvaluatedAt: evaluation.evaluatedAt
          }
        : s
    ));
    
    // Add to history
    setSignalHistory(prev => ({
      ...prev,
      [signalId]: [...(prev[signalId] || []).slice(-99), evaluation]
    }));
    
    // Handle alerts
    if (evaluation.breachedThreshold && evaluation.status !== THRESHOLD_LEVELS.NORMAL) {
      const alert = {
        id: `alert_${signalId}_${Date.now()}`,
        signalId,
        signalName: signal.name,
        evaluation,
        createdAt: new Date().toISOString(),
        acknowledged: false
      };
      setActiveAlerts(prev => [...prev, alert]);
    }
    
    return evaluation;
  }, [signals]);
  
  /**
   * Evaluate all enabled signals
   */
  const evaluateAllSignals = useCallback((valueMap) => {
    const results = [];
    
    signals.filter(s => s.enabled).forEach(signal => {
      const currentValue = valueMap[signal.id];
      if (currentValue !== undefined) {
        const evaluation = evaluateSignal(signal.id, currentValue);
        if (evaluation) results.push(evaluation);
      }
    });
    
    setLastScanAt(new Date().toISOString());
    return results;
  }, [signals, evaluateSignal]);
  
  /**
   * Check drift for a specific signal (without storing)
   */
  const checkDrift = useCallback((signalId, currentValue) => {
    const signal = signals.find(s => s.id === signalId);
    if (!signal) return null;
    return evaluateSignalDrift(signal, currentValue);
  }, [signals]);
  
  // ============================================================================
  // ALERT MANAGEMENT
  // ============================================================================
  
  /**
   * Acknowledge an alert
   */
  const acknowledgeAlert = useCallback((alertId) => {
    setActiveAlerts(prev => prev.map(a => 
      a.id === alertId ? { ...a, acknowledged: true } : a
    ));
  }, []);
  
  /**
   * Dismiss an alert
   */
  const dismissAlert = useCallback((alertId) => {
    setActiveAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);
  
  /**
   * Clear all alerts for a signal
   */
  const clearAlertsForSignal = useCallback((signalId) => {
    setActiveAlerts(prev => prev.filter(a => a.signalId !== signalId));
  }, []);
  
  // ============================================================================
  // PROGRAM SIGNALS
  // ============================================================================
  
  /**
   * Get signals for a specific program
   */
  const getSignalsForProgram = useCallback((programId) => {
    return signals.filter(s => s.programId === programId);
  }, [signals]);
  
  /**
   * Get signals attached to a specific node
   */
  const getSignalsForNode = useCallback((nodeRef) => {
    return signals.filter(s => s.nodeRef === nodeRef);
  }, [signals]);
  
  /**
   * Get signals attached to a specific edge
   */
  const getSignalsForEdge = useCallback((edgeRef) => {
    return signals.filter(s => s.edgeRef === edgeRef);
  }, [signals]);
  
  /**
   * Initialize signals for a program from template
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const initializeProgramSignals = useCallback((programId, templateKey) => {
    // Clear existing signals for this program
    setSignals(prev => prev.filter(s => s.programId !== programId));
    
    // Add new signals from template
    return addSignalsFromTemplate(programId, templateKey);
  }, [addSignalsFromTemplate, setSignals]);
  
  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  /**
   * Signals by status (includes catastrophic level)
   */
  const signalsByStatus = useMemo(() => {
    const grouped = {
      [THRESHOLD_LEVELS.NORMAL]: [],
      [THRESHOLD_LEVELS.WARNING]: [],
      [THRESHOLD_LEVELS.CRITICAL]: [],
      [THRESHOLD_LEVELS.EMERGENCY]: [],
      [THRESHOLD_LEVELS.CATASTROPHIC]: []
    };
    
    signals.forEach(s => {
      const status = s.currentStatus || THRESHOLD_LEVELS.NORMAL;
      if (grouped[status]) {
        grouped[status].push(s);
      } else {
        grouped[THRESHOLD_LEVELS.NORMAL].push(s);
      }
    });
    
    return grouped;
  }, [signals]);
  
  /**
   * System health summary (includes catastrophic level)
   */
  const healthSummary = useMemo(() => {
    const total = signals.filter(s => s.enabled).length;
    const normal = signalsByStatus[THRESHOLD_LEVELS.NORMAL].length;
    const warning = signalsByStatus[THRESHOLD_LEVELS.WARNING].length;
    const critical = signalsByStatus[THRESHOLD_LEVELS.CRITICAL].length;
    const emergency = signalsByStatus[THRESHOLD_LEVELS.EMERGENCY].length;
    const catastrophic = signalsByStatus[THRESHOLD_LEVELS.CATASTROPHIC].length;
    
    let overallStatus = THRESHOLD_LEVELS.NORMAL;
    if (catastrophic > 0) overallStatus = THRESHOLD_LEVELS.CATASTROPHIC;
    else if (emergency > 0) overallStatus = THRESHOLD_LEVELS.EMERGENCY;
    else if (critical > 0) overallStatus = THRESHOLD_LEVELS.CRITICAL;
    else if (warning > 0) overallStatus = THRESHOLD_LEVELS.WARNING;
    
    return {
      total,
      normal,
      warning,
      critical,
      emergency,
      catastrophic,
      overallStatus,
      healthScore: total > 0 ? Math.round((normal / total) * 100) : 100,
      unacknowledgedAlerts: activeAlerts.filter(a => !a.acknowledged).length
    };
  }, [signals, signalsByStatus, activeAlerts]);
  
  /**
   * Monitoring signals count (for Commit Economy)
   */
  const monitoringMetrics = useMemo(() => {
    const structural = signals.filter(s => 
      [SIGNAL_CATEGORIES.CAPACITY, SIGNAL_CATEGORIES.CONCENTRATION].includes(s.category)
    ).length;
    
    const behaviour = signals.filter(s => 
      [SIGNAL_CATEGORIES.VELOCITY, SIGNAL_CATEGORIES.DISTRIBUTION, SIGNAL_CATEGORIES.COMPLIANCE].includes(s.category)
    ).length;
    
    const informational = signals.filter(s => 
      [SIGNAL_CATEGORIES.LATENCY, SIGNAL_CATEGORIES.ANOMALY].includes(s.category)
    ).length;
    
    return {
      structuralSignals: structural,
      behaviourSignals: behaviour,
      informationalSignals: informational,
      totalSignals: signals.length
    };
  }, [signals]);
  
  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  
  const value = {
    // Constants
    SIGNAL_CATEGORIES,
    SIGNAL_CATEGORY_METADATA,
    REFRESH_RATES,
    REFRESH_RATE_METADATA,
    AGGREGATION_METHODS,
    THRESHOLD_LEVELS,
    THRESHOLD_LEVEL_METADATA,
    SIGNAL_TEMPLATES,
    
    // State
    signals,
    signalHistory,
    activeAlerts,
    monitoringEnabled,
    lastScanAt,
    
    // Computed
    signalsByStatus,
    healthSummary,
    monitoringMetrics,
    
    // Signal management
    addSignal,
    addSignalsFromTemplate,
    updateSignal,
    removeSignal,
    toggleSignal,
    attachToNode,
    attachToEdge,
    linkToDecisionMatrix,
    
    // Drift evaluation
    evaluateSignal,
    evaluateAllSignals,
    checkDrift,
    
    // Alert management
    acknowledgeAlert,
    dismissAlert,
    clearAlertsForSignal,
    
    // Program signals
    getSignalsForProgram,
    getSignalsForNode,
    getSignalsForEdge,
    initializeProgramSignals,
    
    // Monitoring control
    setMonitoringEnabled,
    
    // Utility functions
    createBaselineSignal,
    evaluateSignalDrift,
    createDriftEvent
  };
  
  return (
    <BaselineSignalsContext.Provider value={value}>
      {children}
    </BaselineSignalsContext.Provider>
  );
}

export function useBaselineSignals() {
  const context = useContext(BaselineSignalsContext);
  if (!context) {
    throw new Error('useBaselineSignals must be used within BaselineSignalsProvider');
  }
  return context;
}

export default BaselineSignalsContext;
